import { customFetch } from '../../services/apiService';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  ShieldCheck, UserPlus, Trash2, Search, Activity, Clock, 
  User, CheckCircle2, AlertCircle, RefreshCw, Loader2, X, ShieldAlert,
  ChevronLeft, ChevronRight, Filter, SlidersHorizontal, BookOpen, Layers
} from 'lucide-react';
import { UserProfile } from '../../types';
import { useAuth } from '../../AuthProvider';
import CustomDialog from '../CustomDialog';
import { useDebounce } from '../../hooks/useDebounce';
import { ServerPagination } from '../ui/ServerPagination';
import { 
  useAdminList, 
  useAdminLogs, 
  usePromoteAdminMutation, 
  useDemoteAdminMutation 
} from '../../hooks/useQueries';
import { useQueryClient } from '@tanstack/react-query';
import { LedgerTable } from './LedgerTable';

interface LogItem {
  id: string;
  timestamp: string;
  adminEmail: string;
  action: string;
  details: string;
}

export type ActivitySubTab = 'adminlog' | 'ledger';

export const AdminActivity: React.FC = () => {
  const { userProfile: currentUser } = useAuth();
  const queryClient = useQueryClient();

  // Read initial URL params
  const getInitialParams = () => {
    if (typeof window === 'undefined') {
      return { page: 1, limit: 10, search: '', action: 'all', subtab: 'adminlog' as ActivitySubTab };
    }
    const searchParams = new URLSearchParams(window.location.search);
    const subtabParam = (searchParams.get('subtab') || searchParams.get('tab') || 'adminlog').toLowerCase();
    const validSubTab: ActivitySubTab = subtabParam === 'ledger' || subtabParam === 'socai' ? 'ledger' : 'adminlog';

    return {
      page: parseInt(searchParams.get('page') || '1', 10) || 1,
      limit: parseInt(searchParams.get('limit') || '10', 10) || 10,
      search: searchParams.get('search') || '',
      action: searchParams.get('action') || 'all',
      subtab: validSubTab
    };
  };

  const initialParams = useMemo(() => getInitialParams(), []);

  // SubTab Navigation State
  const [activeSubTab, setActiveSubTab] = useState<ActivitySubTab>(initialParams.subtab);

  // 1. State for Pagination & Filters
  const [currentPage, setCurrentPage] = useState<number>(initialParams.page);
  const [pageSize, setPageSize] = useState<number>(initialParams.limit);
  const [searchQuery, setSearchQuery] = useState<string>(initialParams.search);
  const [actionFilter, setActionFilter] = useState<string>(initialParams.action);

  // Debounce search query 400ms
  const debouncedSearch = useDebounce(searchQuery, 400);

  // Track initial mount for resetting page
  const isMountedRef = useRef<boolean>(false);

  // Promote Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchingUser, setSearchingUser] = useState(false);
  const [foundUser, setFoundUser] = useState<UserProfile | null>(null);
  const [searchError, setSearchError] = useState('');

  // Dialog State
  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    type?: 'success' | 'error' | 'warning' | 'confirm';
    title: string;
    message: string;
    onConfirm?: () => void;
  }>({
    isOpen: false,
    title: '',
    message: ''
  });

  // 2. React Query: Fetch Admins List & Paginated Logs
  const { data: admins = [], isLoading: loadingAdmins } = useAdminList();

  const {
    data: logsQueryData,
    isLoading: loadingLogs,
    isFetching: isFetchingLogs,
    refetch: refetchLogs
  } = useAdminLogs({
    page: currentPage,
    limit: pageSize,
    search: debouncedSearch,
    action: actionFilter
  });

  const logs: LogItem[] = logsQueryData?.data || [];
  const totalRecords = logsQueryData?.pagination?.totalCount ?? 0;
  const totalPages = logsQueryData?.pagination?.totalPages ?? 1;

  // React Query Mutations
  const promoteAdminMutation = usePromoteAdminMutation();
  const demoteAdminMutation = useDemoteAdminMutation();

  // 3. URL Sync: Cập nhật ?subtab=x & ?page=x (& limit/search nếu có) vào URL trình duyệt
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      
      if (activeSubTab === 'ledger') {
        url.searchParams.set('subtab', 'ledger');
      } else {
        url.searchParams.delete('subtab');
      }

      if (currentPage > 1) {
        url.searchParams.set('page', String(currentPage));
      } else {
        url.searchParams.delete('page');
      }

      if (pageSize !== 10) {
        url.searchParams.set('limit', String(pageSize));
      } else {
        url.searchParams.delete('limit');
      }

      if (debouncedSearch.trim()) {
        url.searchParams.set('search', debouncedSearch.trim());
      } else {
        url.searchParams.delete('search');
      }

      if (actionFilter !== 'all') {
        url.searchParams.set('action', actionFilter);
      } else {
        url.searchParams.delete('action');
      }

      window.history.replaceState(null, '', url.toString());
    }
  }, [activeSubTab, currentPage, pageSize, debouncedSearch, actionFilter]);

  // Reset to page 1 when filters or search change
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    setCurrentPage(1);
  }, [debouncedSearch, actionFilter]);

  // Listen to popstate for page changes via browser Back/Forward
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handlePopState = () => {
      const searchParams = new URLSearchParams(window.location.search);
      const subtabParam = (searchParams.get('subtab') || searchParams.get('tab') || 'adminlog').toLowerCase();
      const validSubTab: ActivitySubTab = subtabParam === 'ledger' || subtabParam === 'socai' ? 'ledger' : 'adminlog';

      const urlPage = parseInt(searchParams.get('page') || '1', 10) || 1;
      const urlLimit = parseInt(searchParams.get('limit') || '10', 10) || 10;
      const urlSearch = searchParams.get('search') || '';
      const urlAction = searchParams.get('action') || 'all';

      setActiveSubTab(validSubTab);
      setCurrentPage(urlPage);
      setPageSize(urlLimit);
      setSearchQuery(urlSearch);
      setActionFilter(urlAction);
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Search User to Promote
  const handleSearchUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchEmail.trim()) return;

    setSearchingUser(true);
    setSearchError('');
    setFoundUser(null);

    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('nha_giao_so_sso_token') || '' : '';
      const res = await customFetch('/api/admins/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: searchEmail.trim() })
      });

      if (res.ok) {
        const data = await res.json();
        setFoundUser(data.user);
      } else {
        const errData = await res.json();
        setSearchError(errData.message || 'Không tìm thấy tài khoản với email này.');
      }
    } catch (err) {
      setSearchError('Đã xảy ra lỗi kết nối khi tìm kiếm.');
    } finally {
      setSearchingUser(false);
    }
  };

  // Promote User
  const handlePromoteUser = async () => {
    if (!foundUser) return;

    try {
      await promoteAdminMutation.mutateAsync({ email: foundUser.email });
      setShowAddModal(false);
      setSearchEmail('');
      setFoundUser(null);

      setDialog({
        isOpen: true,
        type: 'success',
        title: 'Cấp quyền thành công',
        message: `Đã cấp quyền Quản trị viên cho ${foundUser.email}`
      });
    } catch (err: any) {
      setDialog({
        isOpen: true,
        type: 'error',
        title: 'Lỗi cấp quyền',
        message: err.message || 'Không thể cấp quyền Quản trị viên.'
      });
    }
  };

  // Demote Admin
  const handleDemoteAdmin = (admin: UserProfile) => {
    const adminId = admin.id || admin._id || admin.uid || '';
    const currentAdminId = currentUser?.id || currentUser?._id || currentUser?.uid;

    if (adminId === currentAdminId || admin.email === currentUser?.email) {
      setDialog({
        isOpen: true,
        type: 'warning',
        title: 'Bảo vệ tài khoản',
        message: 'Bạn không thể tự gỡ quyền Admin của chính mình để bảo đảm tính toàn vẹn hệ thống.'
      });
      return;
    }

    setDialog({
      isOpen: true,
      type: 'confirm',
      title: 'Xác nhận gỡ quyền Admin',
      message: `Bạn có chắc chắn muốn gỡ quyền Quản trị viên của ${admin.displayName || admin.email} (${admin.email})?`,
      onConfirm: async () => {
        setDialog(prev => ({ ...prev, isOpen: false }));
        try {
          await demoteAdminMutation.mutateAsync({ userId: adminId });
          setDialog({
            isOpen: true,
            type: 'success',
            title: 'Thành công',
            message: `Đã gỡ quyền Admin của ${admin.email} thành công.`
          });
        } catch (err: any) {
          setDialog({
            isOpen: true,
            type: 'error',
            title: 'Lỗi',
            message: err.message || 'Không thể gỡ quyền Admin.'
          });
        }
      }
    });
  };

  const actionOptions = [
    { value: 'all', label: 'Tất cả hành động' },
    { value: 'Duyệt Thanh toán', label: 'Duyệt Thanh toán' },
    { value: 'Cấp VIP thủ công', label: 'Cấp VIP thủ công' },
    { value: 'Cập nhật Xu', label: 'Cập nhật Xu' },
    { value: 'Cấu hình Hệ thống', label: 'Cấu hình Hệ thống' },
    { value: 'Đồng bộ Google Drive', label: 'Đồng bộ Google Drive' },
    { value: 'Thêm Mã giảm giá', label: 'Thêm Mã giảm giá' },
    { value: 'Cấp quyền Admin', label: 'Cấp quyền Admin' },
    { value: 'Gỡ quyền Admin', label: 'Gỡ quyền Admin' },
    { value: 'Cập nhật Ngân hàng', label: 'Cập nhật Ngân hàng' },
    { value: 'Cập nhật Gói nạp', label: 'Cập nhật Gói nạp' },
    { value: 'Cập nhật Tài liệu', label: 'Cập nhật Tài liệu' },
    { value: 'Từ chối Nạp Xu', label: 'Từ chối Nạp Xu' },
  ];

  return (
    <div className="space-y-6">
      {/* Sub-Tabs Switcher (AdminLog & Sổ Cái) */}
      <div className="bg-white p-2 rounded-2xl border border-slate-200 shadow-2xs flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setActiveSubTab('adminlog')}
          className={`flex-1 sm:flex-initial px-5 py-2.5 rounded-xl text-xs font-extrabold flex items-center justify-center space-x-2 transition-all cursor-pointer ${
            activeSubTab === 'adminlog'
              ? 'bg-purple-600 text-white shadow-xs'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
          }`}
        >
          <Activity className="w-4 h-4" />
          <span>Nhật ký Quản trị (AdminLog)</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveSubTab('ledger')}
          className={`flex-1 sm:flex-initial px-5 py-2.5 rounded-xl text-xs font-extrabold flex items-center justify-center space-x-2 transition-all cursor-pointer ${
            activeSubTab === 'ledger'
              ? 'bg-indigo-600 text-white shadow-xs'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
          }`}
        >
          <BookOpen className="w-4 h-4" />
          <span>Sổ Cái Hệ Thống (Ledger)</span>
        </button>
      </div>

      {activeSubTab === 'ledger' ? (
        <LedgerTable />
      ) : (
        <>
          {/* 1. Danh Sách Quản Trị Viên */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-2xs space-y-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-3 border-b border-slate-100">
          <div>
            <h2 className="text-base font-extrabold text-slate-900 flex items-center space-x-2">
              <ShieldCheck className="w-5 h-5 text-purple-600" />
              <span>Danh sách Quản trị viên (Admins)</span>
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Danh sách tài khoản có toàn quyền quản trị và cấu hình hệ thống
            </p>
          </div>

          <button
            type="button"
            onClick={() => {
              setShowAddModal(true);
              setSearchEmail('');
              setFoundUser(null);
              setSearchError('');
            }}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-xl transition-all shadow-2xs flex items-center space-x-2 shrink-0 cursor-pointer"
          >
            <UserPlus className="w-4 h-4" />
            <span>Thêm Admin mới</span>
          </button>
        </div>

        {/* Grid List Admins */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {loadingAdmins ? (
            <div className="col-span-full py-8 text-center text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-purple-600" />
              <span>Đang tải danh sách Admin...</span>
            </div>
          ) : admins.length === 0 ? (
            <div className="col-span-full py-8 text-center text-slate-400">
              Chưa có tài khoản Admin nào.
            </div>
          ) : (
            admins.map((admin) => {
              const adminId = admin.id || admin._id || admin.uid || '';
              const isSelf = adminId === (currentUser?.id || currentUser?._id || currentUser?.uid) || admin.email === currentUser?.email;

              return (
                <div
                  key={adminId}
                  className="p-3.5 bg-slate-50 hover:bg-purple-50/40 border border-slate-200/80 rounded-2xl flex items-center justify-between transition-colors"
                >
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-purple-100 border border-purple-200 overflow-hidden shrink-0 flex items-center justify-center font-bold text-purple-700">
                      {admin.photoURL ? (
                        <img src={admin.photoURL} alt={admin.displayName} className="w-full h-full object-cover" />
                      ) : (
                        admin.displayName?.slice(0, 2)?.toUpperCase() || 'AD'
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-extrabold text-slate-900 truncate flex items-center space-x-1.5">
                        <span>{admin.displayName}</span>
                        {isSelf && (
                          <span className="text-[9px] px-1.5 py-0.2 bg-emerald-100 text-emerald-800 rounded-md font-extrabold">
                            Bạn
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-400 truncate mt-0.5">{admin.email}</div>
                    </div>
                  </div>

                  {!isSelf && (
                    <button
                      type="button"
                      onClick={() => handleDemoteAdmin(admin)}
                      className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-colors shrink-0 ml-2 cursor-pointer"
                      title="Gỡ quyền Admin"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* 2. Nhật Ký Hoạt Động Quản Trị (Server-Side Paginated Logs) */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
        {/* Header with Title and Search/Filters */}
        <div className="p-5 border-b border-slate-100 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-extrabold text-slate-900 flex items-center space-x-2">
                <Activity className="w-5 h-5 text-indigo-600" />
                <span>Nhật ký Hoạt động Quản trị (Admin Activity Logs)</span>
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Lưu vết lịch sử tác động hệ thống ({totalRecords} hoạt động • Phân trang phía Server)
              </p>
            </div>

            <button
              type="button"
              onClick={() => {
                refetchLogs();
                queryClient.invalidateQueries({ queryKey: ['adminLogs'] });
              }}
              className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-colors self-end sm:self-auto cursor-pointer"
              title="Cập nhật nhật ký"
            >
              <RefreshCw className={`w-4 h-4 ${isFetchingLogs ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Search & Filter Bar */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Keyword Search */}
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Tìm theo email admin, nội dung thao tác, ID..."
                className="w-full pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 focus:bg-white focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 rounded-full cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Action Filter */}
            <div className="w-full sm:w-60">
              <select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-700 focus:bg-white focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all cursor-pointer"
              >
                {actionOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Table Logs */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50/80 border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                <th className="py-3 px-4 w-44">Thời gian</th>
                <th className="py-3 px-4 w-52">Admin thực hiện</th>
                <th className="py-3 px-4 w-48">Hành động</th>
                <th className="py-3 px-4">Chi tiết thao tác</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loadingLogs ? (
                <tr>
                  <td colSpan={4} className="py-12 text-center text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-indigo-600" />
                    <span>Đang tải dữ liệu từ máy chủ...</span>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-12 text-center text-slate-400">
                    {searchQuery || actionFilter !== 'all' 
                      ? 'Không tìm thấy hoạt động nào phù hợp với bộ lọc.'
                      : 'Chưa có lịch sử hoạt động nào được ghi nhận.'}
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="hover:bg-purple-50/20 transition-colors">
                    <td className="py-3 px-4 text-slate-500 font-mono text-[11px] whitespace-nowrap">
                      {new Date(log.timestamp).toLocaleString('vi-VN')}
                    </td>
                    <td className="py-3 px-4 font-bold text-slate-800">
                      <div className="truncate max-w-[200px]" title={log.adminEmail}>
                        {log.adminEmail}
                      </div>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className="px-2.5 py-1 bg-indigo-50 text-indigo-800 font-extrabold text-[11px] rounded-lg border border-indigo-100 inline-block">
                        {log.action}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-slate-600 font-medium leading-relaxed">
                      {log.details}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

          {/* Server Pagination */}
          <ServerPagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalRecords={totalRecords}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={[10, 20, 50]}
            itemLabel="hoạt động"
            themeColor="purple"
          />
        </div>
      </>
    )}

      {/* Modal Add Admin */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-slate-100 relative space-y-4">
            <button
              type="button"
              onClick={() => setShowAddModal(false)}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-2xl bg-purple-100 text-purple-600 flex items-center justify-center shrink-0">
                <UserPlus className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-extrabold text-slate-900">
                  Cấp quyền Quản trị viên
                </h3>
                <p className="text-xs text-slate-500">
                  Nhập chính xác Email tài khoản người dùng để phân quyền Admin
                </p>
              </div>
            </div>

            {/* Search Form */}
            <form onSubmit={handleSearchUser} className="space-y-3 pt-2">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Email người dùng:
                </label>
                <div className="flex space-x-2">
                  <input
                    type="email"
                    value={searchEmail}
                    onChange={e => setSearchEmail(e.target.value)}
                    placeholder="ví dụ: gv@nhagiaoso.com"
                    required
                    className="flex-1 bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-medium text-slate-900 focus:ring-2 focus:ring-purple-500 outline-none"
                  />
                  <button
                    type="submit"
                    disabled={searchingUser}
                    className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition-colors flex items-center space-x-1 shrink-0 cursor-pointer"
                  >
                    {searchingUser ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                    <span>Tìm</span>
                  </button>
                </div>
              </div>
            </form>

            {/* Search Error */}
            {searchError && (
              <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-medium rounded-xl flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 shrink-0 text-rose-500" />
                <span>{searchError}</span>
              </div>
            )}

            {/* Found User Preview */}
            {foundUser && (
              <div className="p-4 bg-purple-50/60 border border-purple-200 rounded-2xl space-y-3 animate-fade-in">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-200 text-purple-800 flex items-center justify-center font-bold text-xs shrink-0">
                    {foundUser.photoURL ? (
                      <img src={foundUser.photoURL} alt={foundUser.displayName} className="w-full h-full object-cover rounded-xl" />
                    ) : (
                      foundUser.displayName?.slice(0, 2)?.toUpperCase() || 'US'
                    )}
                  </div>
                  <div>
                    <div className="text-xs font-extrabold text-slate-900">{foundUser.displayName}</div>
                    <div className="text-[11px] text-slate-500">{foundUser.email}</div>
                  </div>
                </div>

                <div className="text-[11px] text-slate-600 space-y-1 pt-1 border-t border-purple-100">
                  <div>• Khối lớp: <span className="font-bold">{foundUser.gradeLevel || 'Chưa cập nhật'}</span></div>
                  <div>• Đơn vị: <span className="font-bold">{foundUser.workplace || 'Chưa cập nhật'}</span></div>
                  <div>• Vai trò hiện tại: <span className="font-bold text-purple-700">{foundUser.role || 'Thành viên'}</span></div>
                </div>

                <button
                  type="button"
                  disabled={promoteAdminMutation.isPending || foundUser.role === 'admin'}
                  onClick={handlePromoteUser}
                  className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-extrabold text-xs rounded-xl shadow-xs transition-all flex items-center justify-center space-x-2 disabled:opacity-50 cursor-pointer"
                >
                  {promoteAdminMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>{foundUser.role === 'admin' ? 'Tài khoản đã là Admin' : 'Cấp quyền Quản trị ngay'}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Custom Dialog */}
      <CustomDialog
        isOpen={dialog.isOpen}
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        onClose={() => setDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={dialog.onConfirm}
      />
    </div>
  );
};

export default AdminActivity;
